import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Dialog, Box, Typography, IconButton, TextField, Button, CircularProgress, Tooltip, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import RoomIcon from '@material-ui/icons/Room';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';
import { addToast } from '../../store/slices/notificationsSlice';
import { fetchRouteDetail, setReplanningRouteId, setRouteName, setSourceOTIds, addWaypoint, clearRoute } from '../../store/slices/routesSlice';
import { navigateTo, setNeedsRecalculation, setSidebarVisible, setSidebarTab, setShowDetails } from '../../store/slices/uiSlice';
import { reoptimizeRouteAfterOTChange } from '../../utils/routeSync';
import ConfirmDialog from '../common/ConfirmDialog';

// Diálogo "Adicionar OT à Rota". Aplica-se a rotas em status='draft'
// (FASE 1) e está pronto para ser estendido a 'in_progress' (FASE 2 —
// ver fase2_adicionar_ot_em_transito.txt).
//
// Fluxo:
//   1. Lista as OTs do cliente que ainda não estão atribuídas (sem
//      assignedRouteId).
//   2. Operador escolhe uma e confirma.
//   3. POST /routes/:id/add-ot — backend associa a OT à rota.
//   4. Operador é levado ao planner com a rota carregada para
//      reotimizar geometria via "Calcular Rota".

const useStyles = makeStyles(() => ({
  paper: {
    width: '92vw', maxWidth: 720, maxHeight: '88vh',
    borderRadius: 14, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '14px 18px',
    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    borderBottom: '1px solid #bae6fd',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  inProgressBanner: {
    margin: '12px 16px 0',
    padding: '10px 12px',
    background: 'rgba(245,158,11,0.10)',
    border: '1px solid rgba(245,158,11,0.4)',
    borderRadius: 8,
    fontSize: '0.72rem',
    color: '#92400e',
    lineHeight: 1.5,
  },
  body: { flex: 1, overflowY: 'auto', padding: '12px 16px' },
  search: { marginBottom: 12 },
  otRow: {
    padding: '10px 12px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginBottom: 8,
    background: '#fff',
    '&:hover': { borderColor: '#3b82f6', background: '#f0f9ff' },
  },
  otRowSelected: {
    borderColor: '#3b82f6 !important',
    background: '#eff6ff !important',
    boxShadow: '0 0 0 3px rgba(59,130,246,0.15)',
  },
  otHeader: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
  },
  otRef: { fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' },
  otType: {
    fontSize: '0.56rem', fontWeight: 800,
    padding: '1px 7px', borderRadius: 999,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  otLine: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: '0.7rem', color: '#475569',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  empty: {
    padding: '40px 20px', textAlign: 'center',
    color: '#94a3b8', fontSize: '0.84rem',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #e2e8f0',
    background: '#fafbfc',
    display: 'flex', gap: 8, justifyContent: 'flex-end',
    alignItems: 'center',
  },
  cancelBtn: { textTransform: 'none', color: '#64748b', fontWeight: 600 },
  confirmBtn: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none',
    padding: '6px 18px',
    boxShadow: '0 2px 6px rgba(59,130,246,0.30)',
    '&:hover': { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' },
    '&:disabled': {
      background: '#cbd5e1', color: '#fff',
    },
  },
}));

export default function AddOTToRouteDialog({
  open, onClose, routeId, routeName, routeStatus,
  routeVehicleId, routeVehiclePlate, routeDriverId, routeDriverName, routeServiceDate,
  onAdded, // optional callback after successful add
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Popup de confirmação antes de commitar — informa o operador do que vai
  // acontecer (sobretudo em rota EM TRÂNSITO, onde a confirmação re-otimiza
  // e reenvia de imediato para a app do motorista).
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset state when dialog reopens.
  useEffect(() => {
    if (!open) {
      setOrders([]); setSelectedId(null); setSearch(''); setConfirmOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Fetch OTs candidatas: pending (sem nada) E assigned (com veículo/
    // motorista mas sem rota). Excluímos in_transit/delivered/cancelled.
    // Em paralelo para evitar duplo round-trip. Belt-and-braces: filtramos
    // !assignedRouteId no cliente.
    Promise.all([
      fetch('/planner-api/transport-orders?status=pending&limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
      fetch('/planner-api/transport-orders?status=assigned&limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
    ])
      .then(([pData, aData]) => {
        if (cancelled) return;
        const pList = Array.isArray(pData) ? pData : (pData.items || pData.data || []);
        const aList = Array.isArray(aData) ? aData : (aData.items || aData.data || []);
        const merged = [...pList, ...aList].filter(o => !o.assignedRouteId);
        // Ordena: pending primeiro, depois assigned (mais provável o
        // operador escolher por atribuir antes de mexer em OTs já com
        // veículo).
        merged.sort((a, b) => {
          if (a.status === b.status) return 0;
          return a.status === 'pending' ? -1 : 1;
        });
        setOrders(merged);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, token]);

  const filtered = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.toLowerCase();
    return orders.filter(o => {
      const ref = ((o.customRef || '').trim() || o.orderNumber || '').toLowerCase();
      const dn = (o.deliveryName || '').toLowerCase();
      const da = (o.deliveryAddress || '').toLowerCase();
      const cn = (o.customerName || '').toLowerCase();
      return ref.includes(q) || dn.includes(q) || da.includes(q) || cn.includes(q);
    });
  }, [orders, search]);

  // Computa lista de incompatibilidades entre a OT escolhida e a rota destino.
  // Returns: { warnings: [{ icon, label, ot, route }], hasAny: bool }
  const selectedOT = useMemo(() => orders.find(o => o.id === selectedId), [orders, selectedId]);
  const warnings = useMemo(() => {
    if (!selectedOT) return [];
    const out = [];
    // Motorista diferente
    const otDriverId = selectedOT.driverId || null;
    if (otDriverId && routeDriverId && Number(otDriverId) !== Number(routeDriverId)) {
      out.push({
        icon: '👤',
        label: 'Motorista diferente',
        ot: selectedOT.driverName || `Motorista #${otDriverId}`,
        route: routeDriverName || `Motorista #${routeDriverId}`,
        kind: 'driver',
      });
    }
    // Veículo diferente — comparação prioriza matrícula (fonte canónica).
    // O id numérico pode divergir mesmo quando é o mesmo veículo físico:
    // a rota guarda o id local (vehicles.id) enquanto a OT pode guardar o
    // Traccar deviceId (external_id). Antes desta correcção apareciam
    // falsos positivos do tipo "OT tem AE-03-FL · rota tem AE-03-FL".
    const otVehicleId = selectedOT.vehicleId || null;
    const otPlateNorm = (selectedOT.vehiclePlate || '').trim().toUpperCase();
    const routePlateNorm = (routeVehiclePlate || '').trim().toUpperCase();
    const platesMatch = otPlateNorm && routePlateNorm && otPlateNorm === routePlateNorm;
    const idsMatch = otVehicleId && routeVehicleId && Number(otVehicleId) === Number(routeVehicleId);
    const sameVehicle = platesMatch || idsMatch;
    if (otVehicleId && routeVehicleId && !sameVehicle) {
      out.push({
        icon: '🚚',
        label: 'Veículo diferente',
        ot: selectedOT.vehiclePlate || `Veículo #${otVehicleId}`,
        route: routeVehiclePlate || `Veículo #${routeVehicleId}`,
        kind: 'vehicle',
      });
    }
    // Data de serviço diferente — só compara YYYY-MM-DD. Tolerância 0 dias.
    if (selectedOT.serviceDate && routeServiceDate) {
      const otDay = String(selectedOT.serviceDate).slice(0, 10);
      const rDay = String(routeServiceDate).slice(0, 10);
      if (otDay !== rDay) {
        out.push({
          icon: '📅',
          label: 'Data de serviço diferente',
          ot: new Date(selectedOT.serviceDate).toLocaleDateString('pt-PT'),
          route: new Date(routeServiceDate).toLocaleDateString('pt-PT'),
          kind: 'date',
        });
      }
    }
    // Janelas horárias muito diferentes — só avisa se diff > 4h entre
    // window centers (heurística pragmática para evitar ruído).
    const otWinFrom = selectedOT.deliveryWindowFrom || selectedOT.pickupWindowFrom;
    const otWinTo = selectedOT.deliveryWindowTo || selectedOT.pickupWindowTo;
    if (otWinFrom && otWinTo && routeServiceDate) {
      const otCenter = (new Date(otWinFrom).getTime() + new Date(otWinTo).getTime()) / 2;
      const routeCenter = new Date(routeServiceDate).getTime();
      const diffH = Math.abs(otCenter - routeCenter) / 3600000;
      if (diffH > 4) {
        out.push({
          icon: '🕓',
          label: 'Janela horária distante',
          ot: `${new Date(otWinFrom).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}–${new Date(otWinTo).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`,
          route: `~${new Date(routeServiceDate).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`,
          kind: 'window',
        });
      }
    }
    return out;
  }, [selectedOT, routeDriverId, routeDriverName, routeVehicleId, routeVehiclePlate, routeServiceDate]);

  async function handleConfirm() {
    if (!selectedId || !routeId || !selectedOT) return;
    setSubmitting(true);

    // ─── Caminho IN_PROGRESS (Fase 2) ──────────────────────────────────
    // A rota já está a ser executada — não faz sentido o operador
    // pré-visualizar e clicar "Gravar" depois. Commit directo:
    //   1) POST /add-ot — associa a OT, acrescenta o waypoint à rota,
    //      marca a geometria como stale, emite route:ot-added e envia
    //      push ao motorista (já estava no backend).
    //   2) reoptimizeRouteAfterOTChange — reordena por vizinho-mais-próximo
    //      a partir do GPS actual do veículo, mantendo as OTs entregues
    //      fixas. Persiste via /recompute-geometry (que já envia push
    //      "Rota re-otimizada" — o motorista é avisado).
    // Se a re-optimização falhar (sem GPS recente / HERE indisponível),
    // a OT FICA atribuída na rota — o operador vê o erro e pode tentar
    // de novo. Isto é melhor que reverter (race com o motorista que
    // pode ter entregue uma OT entretanto).
    if (routeStatus === 'in_progress') {
      try {
        const addRes = await fetch(`/planner-api/routes/${routeId}/add-ot`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ otId: selectedOT.id }),
        });
        if (!addRes.ok) {
          const j = await addRes.json().catch(() => ({}));
          throw new Error(j.error || 'Falha a adicionar a OT à rota.');
        }
        const lat = Number(selectedOT.deliveryLat);
        const lng = Number(selectedOT.deliveryLng);
        const name = selectedOT.deliveryName || selectedOT.deliveryAddress || null;
        const reopt = (Number.isFinite(lat) && Number.isFinite(lng))
          ? await reoptimizeRouteAfterOTChange({
              routeId, otId: selectedOT.id, lat, lng, name, token,
            })
          : { ok: false, error: 'Sem coordenadas — não foi possível re-otimizar.' };
        if (reopt.ok) {
          dispatch(addToast({
            type: 'success',
            message: `${selectedOT.customRef || selectedOT.orderNumber || `OT-${selectedOT.id}`} adicionada e rota re-otimizada (${reopt.reordered} paragens reordenadas). Motorista notificado.`,
            duration: 6000,
          }));
        } else {
          dispatch(addToast({
            type: 'warning',
            message: `OT adicionada à rota, mas a re-optimização falhou: ${reopt.error}. A rota mantém a ordem actual.`,
            duration: 8000,
          }));
        }
        if (onAdded) onAdded({ otId: selectedOT.id, inProgress: true });
        onClose();
      } catch (e) {
        dispatch(addToast({ type: 'error', message: e.message || 'Erro a adicionar OT' }));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      // Opção A — "Preview antes de gravar":
      //   NÃO chamamos /add-ot aqui. Apenas carregamos a rota no planner
      //   com o waypoint da nova OT em MEMÓRIA (Redux). A persistência
      //   acontece quando o operador clicar "Gravar" / "Gravar e Despachar"
      //   na toolbar do mapa — esses botões chamam saveRouteToAPI que:
      //     · faz PUT à rota com waypoints/geometria actualizados
      //     · chama /transport-orders/:otId/assign para cada OT em sourceOTIds
      //   "Cancelar" no planner descarta tudo limpo — sem touch na BD.
      //
      //   Importante: temos toda a info da OT em selectedOT (id, deliveryLat,
      //   deliveryLng, serviceType, etc) por isso não precisamos do response
      //   do servidor.

      // 1. Carregar a rota actual da BD no Redux com flag de replanning.
      dispatch(clearRoute());
      dispatch(setReplanningRouteId(routeId));
      const apiRoute = await dispatch(fetchRouteDetail({ token, routeId })).unwrap().catch(() => null);

      // 2. Adicionar a nova OT ao array sourceOTIds em Redux. Quando o
      //    operador gravar, saveRouteToAPI envia o array completo e o
      //    backend persiste + faz o assign de cada OT.
      const existingSourceOtIds = Array.isArray(apiRoute?.sourceOtIds) ? apiRoute.sourceOtIds : [];
      const newSourceOtIds = existingSourceOtIds.includes(selectedOT.id)
        ? existingSourceOtIds
        : [...existingSourceOtIds, selectedOT.id];
      dispatch(setSourceOTIds(newSourceOtIds));

      // 3. Adicionar o waypoint visual com toda a metadata da OT
      //    seleccionada (vem do dialog state, não do servidor).
      const lat = Number(selectedOT.deliveryLat);
      const lng = Number(selectedOT.deliveryLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const wpKind = selectedOT.serviceType === 'assistance' ? 'service'
          : selectedOT.serviceType === 'fuel' ? 'fuel'
          : 'delivery';
        dispatch(addWaypoint({
          name: selectedOT.deliveryName || selectedOT.deliveryAddress || selectedOT.customRef || selectedOT.orderNumber || `OT-${selectedOT.id}`,
          coordinates: [lng, lat],
          otId: selectedOT.id,
          orderNumber: selectedOT.orderNumber,
          customRef: (selectedOT.customRef || '').trim() || null,
          waypointKind: wpKind,
          stopDuration: selectedOT.unloadingTime || 30,
        }));
        dispatch(setNeedsRecalculation(true));
      }

      dispatch(addToast({
        type: 'info',
        message: `${selectedOT.customRef || selectedOT.orderNumber || `OT-${selectedOT.id}`} adicionada em pré-visualização. Re-otimize e clica "Gravar" para confirmar — "Cancelar" descarta.`,
        duration: 6000,
      }));

      if (onAdded) onAdded({ otId: selectedOT.id, preview: true });
      onClose();

      dispatch(setSidebarVisible(true));
      dispatch(setSidebarTab(0));
      dispatch(setShowDetails(true));
      dispatch(navigateTo('planner'));
    } catch (e) {
      dispatch(addToast({ type: 'error', message: e.message || 'Erro' }));
    } finally {
      setSubmitting(false);
    }
  }

  const isInProgress = routeStatus === 'in_progress';

  const selOtText = selectedOT
    ? (selectedOT.customRef || selectedOT.orderNumber || `OT-${selectedOT.id}`)
    : 'a ordem';
  const selOtWhere = selectedOT?.deliveryName ? ` (${selectedOT.deliveryName})` : '';

  return (
    <>
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <Box className={classes.header}>
        <Box style={{
          width: 32, height: 32, borderRadius: 10,
          background: 'rgba(59,130,246,0.15)', color: '#2563eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <AddIcon style={{ fontSize: 18 }} />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a' }}>
            Adicionar ordem à rota
          </Typography>
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {routeName || `Rota #${routeId}`}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Banner especial só para in_progress (Fase 2 — ainda não habilitado
          completamente, mas a UI já avisa o operador). */}
      {isInProgress && (
        <Box className={classes.inProgressBanner}>
          ⚠ <b>Esta rota está em trânsito.</b> A nova ordem será inserida
          nas paragens restantes — as já entregues mantêm-se inalteradas.
          O motorista será notificado.
        </Box>
      )}

      <Box className={classes.body}>
        <TextField
          variant="outlined" size="small" fullWidth
          className={classes.search}
          placeholder="Pesquisar por referência, cliente ou morada..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {loading && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && filtered.length === 0 && (
          <Box className={classes.empty}>
            {orders.length === 0
              ? '✓ Sem ordens pendentes — todas estão atribuídas a rotas.'
              : 'Nenhuma ordem corresponde à pesquisa.'}
          </Box>
        )}

        {!loading && filtered.map(o => {
          const ref = (o.customRef || '').trim() || o.orderNumber || `OT-${o.id}`;
          const isSelected = selectedId === o.id;
          const stype = o.serviceType || 'delivery';
          const stCfg = stype === 'assistance'
            ? { color: '#7c3aed', bg: '#ede9fe', label: '🔧 Assistência' }
            : stype === 'fuel'
              ? { color: '#ea580c', bg: '#fed7aa', label: '⛽ Combustível' }
              : { color: '#2563eb', bg: '#dbeafe', label: '📦 Entrega' };
          return (
            <Box
              key={o.id}
              className={`${classes.otRow} ${isSelected ? classes.otRowSelected : ''}`}
              onClick={() => setSelectedId(o.id)}
            >
              <Box className={classes.otHeader}>
                <Typography className={classes.otRef}>{ref}</Typography>
                <span className={classes.otType} style={{ color: stCfg.color, background: stCfg.bg }}>
                  {stCfg.label}
                </span>
                {o.serviceDate && (
                  <Chip
                    size="small"
                    label={new Date(o.serviceDate).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                    style={{ height: 20, fontSize: '0.6rem', background: '#f0fdf4', color: '#166534' }}
                  />
                )}
              </Box>
              <Box className={classes.otLine}>
                <RoomIcon style={{ fontSize: 13, color: '#dc2626' }} />
                <span>{o.deliveryName || o.deliveryAddress || '—'}</span>
              </Box>
              {o.customerName && (
                <Box className={classes.otLine} style={{ color: '#64748b', fontSize: '0.62rem' }}>
                  <PersonIcon style={{ fontSize: 11 }} />
                  <span>{o.customerName}</span>
                </Box>
              )}
              {/* Veículo + motorista actuais (só para status='assigned').
                  OTs pending ficam sem esta linha — não têm nada atribuído. */}
              {(o.vehiclePlate || o.driverName) && (
                <Box className={classes.otLine} style={{ color: '#475569', fontSize: '0.62rem', marginTop: 2, gap: 8 }}>
                  {o.vehiclePlate && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <LocalShippingIcon style={{ fontSize: 11, color: '#64748b' }} />
                      <b>{o.vehiclePlate}</b>
                    </span>
                  )}
                  {o.driverName && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <PersonIcon style={{ fontSize: 11, color: '#64748b' }} />
                      {o.driverName}
                    </span>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Banner de incompatibilidades — só aparece se houver pelo menos 1
          warning entre a OT seleccionada e a rota destino. Operador pode
          prosseguir mesmo assim (decisão dele). */}
      {warnings.length > 0 && (
        <Box style={{
          margin: '0 16px 8px',
          padding: '10px 12px',
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          border: '1px solid #fcd34d',
          borderRadius: 8,
        }}>
          <Typography style={{ fontSize: '0.74rem', fontWeight: 800, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚠ {warnings.length === 1 ? 'Incompatibilidade detectada' : `${warnings.length} incompatibilidades detectadas`}
          </Typography>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {warnings.map((w, idx) => (
              <Box key={idx} style={{ fontSize: '0.68rem', color: '#78350f', lineHeight: 1.5 }}>
                <span style={{ marginRight: 4 }}>{w.icon}</span>
                <b>{w.label}:</b> OT tem <i>{w.ot}</i> · rota tem <i>{w.route}</i>
              </Box>
            ))}
          </Box>
          <Typography style={{ fontSize: '0.62rem', color: '#a16207', marginTop: 6, fontStyle: 'italic' }}>
            Podes prosseguir — o veículo/motorista da OT será substituído pelos da rota e a janela horária será ajustada na re-otimização.
          </Typography>
        </Box>
      )}

      <Box className={classes.footer}>
        <Box style={{ flex: 1, fontSize: '0.66rem', color: '#94a3b8' }}>
          {selectedId
            ? (isInProgress
                ? '1 ordem seleccionada · vai entrar na rota EM TRÂNSITO e o motorista é notificado'
                : '1 ordem seleccionada · será adicionada à rota e reotimizada no planeamento')
            : 'Escolha uma ordem da lista'}
        </Box>
        <Button
          className={classes.cancelBtn}
          onClick={onClose}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Tooltip title={!selectedId
          ? 'Escolha uma ordem primeiro'
          : (isInProgress
              ? 'Adicionar à rota em trânsito, re-otimizar a partir do GPS e notificar o motorista'
              : 'Adicionar ordem à rota e abrir no planeamento')}>
          <span>
            <Button
              className={classes.confirmBtn}
              onClick={() => setConfirmOpen(true)}
              disabled={!selectedId || submitting}
            >
              {submitting
                ? <CircularProgress size={16} style={{ color: '#fff' }} />
                : (isInProgress ? 'Adicionar e notificar motorista' : 'Adicionar e abrir no Planeamento')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Dialog>

    {/* Confirmação antes de commitar — informa o operador do que vai
        acontecer. Em rota EM TRÂNSITO a confirmação re-otimiza e reenvia
        de imediato para o motorista; em draft/planned abre no planeamento. */}
    <ConfirmDialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      onConfirm={handleConfirm}
      severity={isInProgress ? 'warning' : 'info'}
      title={isInProgress ? 'Adicionar OT a uma rota EM CURSO?' : 'Adicionar OT à rota?'}
      message={isInProgress
        ? `${selOtText}${selOtWhere} vai ser adicionada a esta rota em trânsito. A rota será re-otimizada a partir da posição atual do motorista (mantendo as paragens já entregues), recalculada e reenviada de imediato para a app dele — o motorista é notificado. Confirmas?`
        : `${selOtText}${selOtWhere} vai ser adicionada à rota e carregada no planeamento. Depois recalcula e grava/despacha para o motorista receber a versão atualizada.`}
      confirmLabel={isInProgress ? 'Adicionar e notificar' : 'Adicionar'}
      cancelLabel="Cancelar"
    />
    </>
  );
}
